import axios from "axios";

class ApiService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || "/api";
    this.client = axios.create({
      baseURL: this.baseURL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    // Response interceptor to handle errors
    this.client.interceptors.response.use(
      (response) => {
        // For blob responses, return the full response to access headers
        if (response.config.responseType === "blob") {
          return response;
        }
        return response.data;
      },
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid
          this.clearToken();
          window.location.href = "/login";
        }
        return Promise.reject(error);
      },
    );
  }

  setToken(token) {
    this.token = token;
  }

  getToken() {
    return this.token || localStorage.getItem("firewatch_token");
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem("firewatch_token");
  }

  // Auth endpoints
  async login(email, password) {
    return this.client.post("/auth/login", { email, password });
  }

  async getCurrentUser() {
    return this.client.get("/auth/me");
  }

  async changePassword(currentPassword, newPassword) {
    return this.client.post("/auth/change-password", {
      current_password: currentPassword,
      new_password: newPassword,
    });
  }

  // Fire endpoints
  async getFires(params = {}) {
    return this.client.get("/fires", { params });
  }

  async getFire(fireId) {
    return this.client.get(`/fires/${fireId}`);
  }

  async getFogosPhotos(fireId, params = {}) {
    return this.client.get(`/fires/${fireId}/fogos-photos`, { params });
  }

  // Sync fires
  async syncFires(useActiveEndpoint = true) {
    return this.client.post("/fires/sync", {
      use_active_endpoint: useActiveEndpoint,
    });
  }

  // Sync interval management
  async getSyncInterval() {
    return this.client.get("/fires/sync/interval");
  }

  async updateSyncInterval(intervalSeconds) {
    return this.client.put("/fires/sync/interval", {
      interval_seconds: intervalSeconds,
    });
  }

  async getDistricts(params = {}) {
    return this.client.get("/fires/districts", { params });
  }

  async getStatuses(params = {}) {
    return this.client.get("/fires/statuses", { params });
  }

  async getFireStats() {
    return this.client.get("/fires/stats");
  }

  // Photo endpoints
  async getPhotos(params = {}) {
    return this.client.get("/photos", { params });
  }

  async getPhoto(photoId) {
    return this.client.get(`/photos/${photoId}`);
  }

  async uploadPhoto(formData) {
    return this.client.post("/photos/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  async deletePhoto(photoId) {
    return this.client.delete(`/photos/${photoId}`);
  }

  async getMyPhotos(params = {}) {
    return this.client.get("/photos/my-photos", { params });
  }

  async approvePhoto(photoId, approve = true) {
    return this.client.post(`/photos/${photoId}/approve`, { approve });
  }

  getPhotoUrl(photoId) {
    return `${this.baseURL}/photos/${photoId}/file`;
  }

  // User endpoints
  async getUsers(params = {}) {
    return this.client.get("/users/", { params });
  }

  async getUser(userId) {
    return this.client.get(`/users/${userId}`);
  }

  async updateUser(userId, userData) {
    return this.client.put(`/users/${userId}`, userData);
  }

  async toggleUserStatus(userId) {
    return this.client.post(`/users/${userId}/toggle-status`);
  }

  async createUser(userData) {
    return this.client.post("/auth/register", userData);
  }

  async getUserStats() {
    return this.client.get("/users/stats");
  }

  async deleteUser(userId) {
    return this.client.delete(`/users/${userId}`);
  }

  async resetUserPassword(userId, password) {
    const response = await this.client.post(`/users/${userId}/reset-password`, {
      new_password: password,
    });
    return response.message;
  }

  async downloadBulkPhotos(fireId = null) {
    try {
      const response = await this.client.post(
        "/photos/download/bulk",
        fireId ? { fire_id: fireId } : {},
        {
          responseType: "blob",
          timeout: 300000, // 5 minutes timeout for large downloads
        },
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;

      // Extract filename from response headers or use default
      const contentDisposition = response.headers["content-disposition"];
      let filename = "FIREWATCH_Fotos.zip";
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true, filename };
    } catch (error) {
      console.error("Bulk download error:", error);
      throw error;
    }
  }

  // Health check
  async healthCheck() {
    return this.client.get("/health");
  }

  // Moderation (fogosapi photo queue) — BFF endpoints
  async listModerationPhotos(params = {}) {
    return this.client.get("/moderation/photos", { params });
  }

  async getModerationPhoto(photoId) {
    return this.client.get(`/moderation/photos/${photoId}`);
  }

  async approveModerationPhoto(photoId, payload = {}) {
    return this.client.post(`/moderation/photos/${photoId}/approve`, payload);
  }

  async rejectModerationPhoto(photoId, payload) {
    return this.client.post(`/moderation/photos/${photoId}/reject`, payload);
  }

  async getModerationPendingCount() {
    return this.client.get("/moderation/pending-count");
  }

  async getModerationAudit(params = {}) {
    return this.client.get("/moderation/audit", { params });
  }

  async getModerationSettings() {
    return this.client.get("/moderation/settings");
  }

  async updateModerationSettings(data) {
    return this.client.put("/moderation/settings", data);
  }

  // fogos.pt public photo feed (BFF — write key stays server-side)
  async getFogosLatestPhotos({ page = 1, per_page = 30 } = {}) {
    return this.client.get("/fogos-photos/latest", {
      params: { page, per_page },
    });
  }

  async deleteFogosPhoto(photoId) {
    return this.client.delete(`/fogos-photos/${photoId}`);
  }

  async getApiTokens() {
    return this.client.get("/admin/api-tokens");
  }

  async createApiToken(data) {
    return this.client.post("/admin/api-tokens", data);
  }

  async revokeApiToken(tokenId) {
    return this.client.post(`/admin/api-tokens/${tokenId}/revoke`);
  }
}

export const apiService = new ApiService();
